/*
 * Voyado card renderer — clones the <template data-tmpl-card*> markup from
 * snippets/voyado-card-templates.liquid and fills the [data-slot] hooks from
 * the Voyado product shape. renderCard(product, opts) → <article data-voyado-card>.
 *
 * The markup is a byte-identical MIRROR of snippets/product-card*.liquid (same
 * compiled CSS), and assets/voyado-card-behaviour.js reuses product-card.js's
 * initCard (window.BroraProductCardVariant) for identical behaviour. Zero
 * class-string work here — every class lives in the .liquid template.
 *
 * opts.quickAdd — gates quick-add + arrows + counter (PLP/search only).
 * opts.siblings — the colour group; renders one swatch per sibling and appends
 *   each sibling's first image (hover swaps the main image, click navigates to
 *   that colour; mirrors Liquid product-card-with-colour-group).
 * No opts (autocomplete drawer) → a simple card: gallery + title + price +
 *   wishlist. Swatch dots show swatch_image_url, falling back to the hex.
 */
;(function () {
  'use strict';

  // Shared DOM-cloner helpers — single source of truth. This renderer is global
  // (loaded before voyado-facet-renderer.js / voyado-plp-controller.js), so it
  // owns these and exposes them on window for those PLP/search consumers.
  // tmpl(name) clones <template data-tmpl-name>'s first element.
  function tmpl(name) {
    const t = document.querySelector('[data-tmpl-' + name + ']');
    const node = t && t.content && t.content.firstElementChild;
    return node ? node.cloneNode(true) : null;
  }
  function slot(root, name) {
    return root && root.querySelector('[data-slot="' + name + '"]');
  }
  window.voyadoTmpl = tmpl;
  window.voyadoSlot = slot;

  // Allow only http(s), protocol-relative or root/relative URLs from the Voyado
  // payload before they reach href/src/background-image — rejects javascript:/
  // vbscript:/data: schemes (TECH-DEBT #56 + the swatch url() break-out).
  function safeUrl(url) {
    // Strip ASCII control chars (tab/newline/CR/DEL) the browser removes when it
    // resolves a URL — otherwise "java\tscript:" slips the scheme test below and
    // resolves to an executable javascript: at the href/src/url() sink.
    const s = (url == null ? '' : String(url)).replace(/[\t\n\r]/g, '').trim();
    if (!s) return '';
    if (/^(https?:)?\/\//i.test(s)) return s;   // absolute or protocol-relative
    if (/^[/?#]/.test(s)) return s;             // root-relative / query / hash
    if (/^[\w.+-]+:/.test(s)) return '';        // any other explicit scheme → reject
    return s;                                   // bare relative path
  }
  window.voyadoSafeUrl = safeUrl; // shared (e.g. autocomplete Pages links)

  // Voyado returns a SINGLE source per image = the full-res Shopify CDN original
  // (e.g. 2500×3168). Shopify CDN URLs honour a `width` param, so synthesise a
  // responsive srcset rather than ship the 2500px original into a card slot. Non-CDN
  // URLs (or an Elevate config that ever returns pre-sized sources) pass through.
  // Ladder derived from the real slot, not from a guess (N-13's rule: solve the layout formula, don't
  // snapshot it). These cards fill the widest card slots in the theme — the PLP/search/wishlist grid —
  // and container-grid pads by --spacing-margin-lg (32) with gap --spacing-gutter-lg (8) at lg:
  //   tile view    (grid-cols-4)                     slot = (100vw - 64 - 3*8)/4  = 25vw     - 22px
  //   gallery view (?view=gallery, _voyado-grid.css) slot = (100vw - 64 - 2*8)/3  = 33.3333vw - 26.6667px
  // At 1512px that is 356px / 477px, so a 2x-DPR device needs 712px / 955px of source. 640 was short at
  // BOTH column counts and visibly short in gallery view — do not drop 960 again. 1280 is the top rung
  // because sizes asks at most 25vw*DPR = 1280 on a 2560px 2x display (the largest desktop config the
  // theme reasons about), and because D71 recommends a 1600px-wide 4:5 upload, so a higher rung could
  // not be served anyway. Deliberately one rung longer than snippets/product-card-image.liquid — the
  // two are no longer meant to be identical: that snippet renders on no PLP surface, and its cards only
  // ever sit in narrower carousel/stacked slots, so it has no use for 1280.
  const IMG_WIDTHS = [320, 480, 640, 960, 1280];
  const IMG_SRC_W = 640;
  function isShopifyCdn(url) { return /\/\/[^/]*cdn\.shopify\.com\//i.test(url || ''); }
  function cdnWidth(url, w) {
    if (!isShopifyCdn(url)) return url;
    const clean = url.replace(/([?&])width=\d+&?/i, '$1').replace(/[?&]$/, '');
    return clean + (clean.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }
  function cdnSrcset(url) {
    if (!isShopifyCdn(url)) return '';
    return IMG_WIDTHS.map(function (w) { return cdnWidth(url, w) + ' ' + w + 'w'; }).join(', ');
  }

  // Formats through the shared market-aware formatter the price corrector exposes, so the number the
  // card is born with already matches the one the correction writes over it.
  function formatPrice(p) {
    if (!p) return '';
    const bv = window.broraVoyado || {};
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || bv.currencyCode;
    let val;
    if (typeof bv.formatMoney === 'function') {
      val = bv.formatMoney(p.min, currency);
    } else if (currency) {
      val = new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency }).format(p.min);
    } else {
      val = String(p.min);
    }
    return p.min === p.max ? val : ('from ' + val);
  }

  // The Elevate feed carries market-agnostic paths, so every card link needs the active market root
  // prepended — without it the shopper drops out of their market on the way to the product.
  function marketRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // safeUrl first: prefixing an unvalidated value would turn a rejected scheme into a path the
  // allowlist then accepts.
  function productUrl(product) {
    const safe = safeUrl(product.link || '');
    return (safe && withMarket(safe)) || '#';
  }

  // Idempotent, and takes either a path or an absolute URL. A missing leading slash is normalised
  // in, so a feed value can be passed straight through.
  function withMarket(link) {
    if (!link) return link;
    const root = marketRoot();

    if (link.indexOf('://') !== -1) {
      try {
        const url = new URL(link);
        if (root === '/' || url.pathname.indexOf(root) === 0) return link;
        url.pathname = root.replace(/\/$/, '') + url.pathname;
        return url.toString();
      } catch (e) {
        return link;
      }
    }

    const path = link[0] === '/' ? link : '/' + link;
    if (root === '/' || path.indexOf(root) === 0) return path;
    return root.replace(/\/$/, '') + path;
  }
  window.voyadoWithMarket = withMarket; // shared (mini-bag recs, autocomplete Pages, quick-add PDP route)

  // Shopify variant id from the Voyado variant link (?variant=<id>) — NOT
  // variant.key (that's the Elevate SKU key, not a Shopify id).
  function variantId(v) {
    const m = v && v.link && v.link.match(/[?&]variant=(\d+)/);
    return m ? m[1] : '';
  }

  // Builds gallery slides from the product's OWN images only. Colour siblings are
  // previewed via the [data-card-preview] overlay (see fillSwatches), not slides.
  // Counter/arrows render when `gallery` is true.
  function fillGallery(root, product, gallery, priority, videoEnabled) {
    const scroll = slot(root, 'gallery-scroll');
    if (!scroll) return;
    let count = 0;
    // Elevate carries a product video as an imageInfo entry with custom.type "Video"
    // + custom.videoUrl (a card-sized SD mp4). Render it as a <video> slide where
    // enabled (PLP/carousels); the drawer passes videoEnabled=false and it falls
    // back to its poster image, same as an ordinary slide.
    function isVideo(image) {
      const c = image && image.custom;
      return videoEnabled && c && c.type === 'Video' && c.videoUrl;
    }
    function addSlide(image, href, alt, ticket) {
      const video = isVideo(image);
      const node = tmpl(video ? 'card-slide-video' : 'card-slide');
      if (!node) return;
      node.setAttribute('data-image-index', count);
      if (count !== 0) { node.setAttribute('aria-hidden', 'true'); node.toggleAttribute('inert', true); }
      const link = slot(node, 'link');
      if (link) {
        link.href = safeUrl(href);
        // Mirror the title link's Voyado click ticket so clicking the image (the
        // primary click target, z-10 above the title's stretched ::before) fires
        // the click notify too — not just the title text. Every slide carries the
        // CURRENT product's ticket — all slides link to the current product (see
        // the siblings loop), so the click attributes to it regardless of preview.
        link.setAttribute('data-voyado-ticket', ticket || '');
      }
      const src0 = (image && image.sources && image.sources[0]) || {};
      if (video) {
        const vid = slot(node, 'video');
        if (vid) {
          // src stays in data-video-src until the slide is on-screen (voyado-card-video.js).
          vid.setAttribute('data-video-src', safeUrl(image.custom.videoUrl));
          const poster = safeUrl(src0.url || '');
          if (poster) vid.setAttribute('poster', isShopifyCdn(poster) ? cdnWidth(poster, IMG_SRC_W) : poster);
          const vw = parseInt(image.custom.videoWidth, 10);
          const vh = parseInt(image.custom.videoHeight, 10);
          if (vw) vid.width = vw;
          if (vh) vid.height = vh;
        }
      } else {
        const img = slot(node, 'image');
        if (img) {
          const safe = safeUrl(src0.url || '');
          const resolved = isShopifyCdn(safe) ? cdnWidth(safe, IMG_SRC_W) : safe;
          const ss = cdnSrcset(safe);
          img.alt = alt || '';
          if (src0.width) img.width = src0.width;
          if (src0.height) img.height = src0.height;
          if (count === 0) {
            img.src = resolved;
            if (ss) img.srcset = ss;
            // First slide of the first card on a reset render is the LCP image:
            // load it eagerly + prioritised instead of the template's lazy default
            // (cards are injected after fetch, so the browser can't discover it early).
            if (priority) {
              img.loading = 'eager';
              img.setAttribute('fetchpriority', 'high');
            }
          } else {
            // Later slides are stacked over the first at lg, so the template's lazy attribute defers
            // nothing — park the URLs and let product-card-variant.js promote them on demand.
            img.setAttribute('data-src', resolved);
            if (ss) img.setAttribute('data-srcset', ss);
          }
        }
      }
      scroll.appendChild(node);
      count++;
    }
    const info = product.imageInfo || {};
    let images = info.images || [];
    if (!images.length) images = [{ sources: [{ url: info.thumbnail || '' }] }];
    const url = productUrl(product);
    images.forEach(function (image) {
      addSlide(image, url, product.title || '', product.ticket);
    });
    if (count > 1) {
      root.setAttribute('data-gallery-enabled', '');
      const total = slot(root, 'total');
      if (total) total.textContent = String(count);
    }
    if (!(gallery && count > 1)) {
      ['counter', 'prev', 'next'].forEach(function (s) {
        const el = slot(root, s);
        if (el) el.remove();
      });
    }
  }

  // Badges mirror snippets/product-card-badges.liquid: manual entries from the
  // custom.product_badges metafield (flowed by the Accelerator) + the automatic
  // Out of stock / Low stock. Low stock uses the per-variant stock quantity
  // (v.stockNumber) + thresholds from window.BroraBadgeConfig, and is skipped
  // when the feed doesn't carry stock numbers. The pre-order badge is added later by
  // voyado-card-quick-add.js's revealPreorderPills — the feed carries no pre-order signal, so it
  // needs the deferred product.js fetch.
  // Liquid's `handle` filter, for the badge hook: lower-cased, non-alphanumerics collapsed to hyphens.
  function badgeHandle(label) {
    return String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function fillBadges(root, product) {
    const container = slot(root, 'badges');
    if (!container) return;
    const labels = [];
    const manual = (product.custom && product.custom.product_badges) || [];
    manual.forEach(function (b) { if (b && b.label) labels.push(b.label); });

    const variants = product.variants || [];
    if (variants.length) {
      const available = variants.some(function (v) { return v && v.inStock; });
      if (!available) {
        labels.push('Out of stock');
      } else {
        const cfg = window.BroraBadgeConfig || {};
        const lowUnits = cfg.lowUnits > 0 ? cfg.lowUnits : 5;
        const lowCountMin = cfg.lowCountMin > 0 ? cfg.lowCountMin : 4;
        const lowTotalMax = cfg.lowTotalMax > 0 ? cfg.lowTotalMax : 15;
        if (variants.length > 1) {
          let lowCount = 0, total = 0, tracked = false;
          variants.forEach(function (v) {
            if (v && typeof v.stockNumber === 'number') {
              tracked = true;
              if (v.stockNumber <= lowUnits) lowCount++;
              total += v.stockNumber;
            }
          });
          if (tracked && (lowCount >= lowCountMin || total <= lowTotalMax)) labels.push('Low stock');
        } else if (typeof variants[0].stockNumber === 'number' && variants[0].stockNumber <= lowUnits) {
          labels.push('Low stock');
        }
      }
    }

    // Keep the (empty) container even with no synchronous badges — revealPreorderPills may add a
    // pre-order badge into it after its deferred product.js fetch.
    if (labels.length === 0) return;
    labels.forEach(function (label) {
      const node = tmpl('card-badge');
      if (!node) return;
      node.textContent = label;
      // Mirrors the Liquid card's `data-card-badge="{{ tag | handle }}"`, so consumers key off the
      // badge kind rather than its displayed text.
      node.setAttribute('data-card-badge', badgeHandle(label));
      container.appendChild(node);
    });
  }

  function fillContent(root, product) {
    const link = slot(root, 'title');
    if (link) {
      link.href = safeUrl(productUrl(product));
      link.setAttribute('data-voyado-ticket', product.ticket || '');
      link.textContent = product.title || product.name || '';
    }
    const price = slot(root, 'price');
    if (price) price.textContent = formatPrice(product.sellingPrice);
    const compare = slot(root, 'compare');
    if (compare) {
      if (product.listPrice && product.sellingPrice && product.listPrice.min > product.sellingPrice.min) {
        compare.textContent = formatPrice(product.listPrice);
      } else {
        // The feed reports no sale for this product, on any market. The slot is kept for the Storefront
        // corrector (voyado-price-storefront.js), which writes the real compare-at price or removes it.
        compare.textContent = '';
      }
    }
  }

  function fillWishlist(root, product) {
    const wishlist = slot(root, 'wishlist');
    if (!wishlist) return;
    // product.key is the Shopify product id (numeric string, e.g. "15912915730757").
    // Wishlist is product-level: set only product_id — no variant fallback (the card
    // represents the whole product, not an arbitrary first variant). The binder Number()s
    // the key; a non-numeric key falls back to undefined.
    if (product.key) wishlist.setAttribute('data-wishlist-product-id', product.key);
    wishlist.setAttribute('aria-label', 'Add ' + (product.title || '') + ' to wishlist');
    wishlist.setAttribute('data-wishlist-label-add', 'Add ' + (product.title || '') + ' to wishlist');
    wishlist.setAttribute('data-wishlist-label-remove', 'Remove ' + (product.title || '') + ' from wishlist');
  }

  function swatchImage(p) {
    const a = p && p.custom && p.custom.swatch_image_url;
    return (a && a[0] && a[0].label) || '';
  }
  function swatchColor(p) {
    const c = p && p.swatch && p.swatch.colors;
    return (c && c[0]) || '';
  }
  // Colour name from custom.colour_name (mapped in the Accelerator feed). Empty
  // until the feed carries it — the tooltip stays off, no error.
  function colourName(p) {
    const a = p && p.custom && p.custom.colour_name;
    return (a && a[0] && (a[0].label || a[0].id)) || '';
  }

  // First OWN image of a colour sibling, used as the hover-preview overlay.
  function siblingPreview(p) {
    const imgs = (p && p.imageInfo && p.imageInfo.images) || [];
    const src = (imgs[0] && imgs[0].sources && imgs[0].sources[0]) || null;
    const u = safeUrl((src && src.url) || '');
    return isShopifyCdn(u) ? cdnWidth(u, 960) : u;
  }

  // One swatch per colour sibling (siblings[0] = the rendered product).
  // The dot shows the sibling's swatch_image_url; if absent, FALLS BACK to its hex.
  // data-swatch-preview carries the colour's image for the hover overlay (blank for
  // the current colour), and data-swatch-url navigates to it on click — both driven
  // by window.BroraProductCardVariant.bindSwatches.
  function fillSwatches(root, product, siblings) {
    const container = slot(root, 'swatches');
    const rowWrap = slot(root, 'swatches-row');
    const extraEl = slot(root, 'swatches-extra');
    if (!container) return;
    const list = (siblings && siblings.length) ? siblings : [product];
    const MAX_SWATCHES = 4;
    let rendered = 0; // paintable colours actually shown (capped)
    let paintable = 0; // paintable colours available (for the +N overflow count)
    list.forEach(function (p, j) {
      const image = swatchImage(p);
      const hex = swatchColor(p);
      if (!image && !hex) return; // nothing to paint this colour
      paintable++;
      if (rendered >= MAX_SWATCHES) return; // cap the visible swatches; count the rest as overflow
      const node = tmpl('card-swatch');
      if (!node) return;
      node.setAttribute('aria-current', j === 0 ? 'true' : 'false');
      // Colour name → native title tooltip + aria-label, mirroring the Liquid swatch.
      const name = colourName(p);
      if (name) { node.setAttribute('title', name); node.setAttribute('aria-label', name); }
      // Current colour (j===0) reveals its own gallery on hover (no overlay);
      // siblings carry their first image as the preview URL.
      if (j > 0) { const pv = siblingPreview(p); if (pv) node.setAttribute('data-swatch-preview', pv); }
      const u = safeUrl(productUrl(p));
      if (u && u !== '#') node.setAttribute('data-swatch-url', u);
      // Each colour is its own Elevate product with its own ticket, so clicking a
      // swatch fires a Click notification for THAT colour (not the current product's)
      // via the document-level [data-voyado-ticket] seam in voyado-client.js.
      if (p.ticket) node.setAttribute('data-voyado-ticket', p.ticket);
      const dot = slot(node, 'dot');
      if (dot) {
        // Validate scheme AND reject quotes/parens/whitespace. The URL no longer lands in a
        // url("…") string — it is the source attribute of the dot's image element — so the
        // string-breakout surface is gone, but the check stays as defence in depth. Fall
        // back to the hex when unsafe/absent, exactly as before. N-11: this dot renders at
        // ~24px — cap the fetch to match, same helper the gallery slides already use.
        let safeImg = safeUrl(image);
        if (safeImg && isShopifyCdn(safeImg)) safeImg = cdnWidth(safeImg, 64);
        if (safeImg && !/["'()\s]/.test(safeImg)) {
          // Real image element instead of a CSS background: a background has no native
          // deferral, and the swatch row is lg:opacity-0 rather than display:none, so it
          // keeps a layout box and every swatch downloads at first paint across the whole
          // grid. The template ships the element with no source at all. TASK-53656 N-11-b.
          const dotImg = slot(node, 'dot-image');
          if (dotImg) dotImg.src = safeImg;
        } else if (hex) {
          dot.style.backgroundColor = hex;
        }
      }
      container.appendChild(node);
      rendered++;
    });
    const extra = paintable - rendered;
    if (extraEl) {
      if (extra > 0) {
        extraEl.textContent = '+' + extra;
        const purl = safeUrl(productUrl(product));
        if (purl && purl !== '#') extraEl.setAttribute('href', purl);
        extraEl.setAttribute('aria-label', 'See ' + extra + ' more colours');
        // The "+N" links to the current product's PDP (same entity as the title/image
        // links), so it fires the same Elevate click ticket via the document-level
        // [data-voyado-ticket] listener in voyado-client.js.
        extraEl.setAttribute('data-voyado-ticket', product.ticket || '');
        extraEl.classList.remove('hidden');
      } else {
        extraEl.remove();
      }
    }
    if (rendered === 0) {
      if (rowWrap) rowWrap.remove(); else container.remove();
    }
  }

  // Quick-add (PLP/search only) lives in voyado-card-quick-add.js (file-size
  // cap) — mirrors product-card-quick-add. We pass the shared cloner helpers.
  function fillQuickAdd(root, product) {
    const qa = window.BroraVoyadoCardQuickAdd;
    if (qa && qa.fill) qa.fill(root, product, { tmpl: tmpl, slot: slot, variantId: variantId, safeUrl: safeUrl, withMarket: withMarket });
  }

  function renderCard(product, opts) {
    const root = tmpl('card');
    if (!root) return document.createElement('article');
    const quickAdd = !!(opts && opts.quickAdd);
    // Gallery chrome (counter + prev/next arrows) is independent of quick-add:
    // the search drawer has the navigable gallery but no quick-add (mirrors
    // product-card-gallery.liquid, which gates arrows/counter on total_images,
    // not show_quick_add). Defaults to quickAdd so PLP callers are unchanged.
    const gallery = (opts && opts.gallery != null) ? !!opts.gallery : quickAdd;
    // Play product videos everywhere the card renders EXCEPT where the caller opts out
    // (the search autocomplete drawer passes video:false → the video shows as its poster).
    const video = !(opts && opts.video === false);
    const siblings = (opts && opts.siblings) || [product];
    fillGallery(root, product, gallery, !!(opts && opts.priority), video);
    fillBadges(root, product);
    fillContent(root, product);
    fillWishlist(root, product);
    fillSwatches(root, product, siblings);
    if (quickAdd) fillQuickAdd(root, product); // always (even OOS) — sizes render disabled, like product-card
    return root;
  }

  window.renderVoyadoCard = renderCard;
})();
